/**
 * Iter-246 — Admin Settings: Guardian Garage Floors Pass Threshold.
 *
 * Exposes a single admin-configurable knob: the percentage required to
 * pass the GGF certification assessment. Default 80%, range 50–100.
 * Changes apply immediately (no cache).
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { ArrowLeft, Save, Loader2, ShieldCheck, Info } from 'lucide-react';
import { toast } from 'sonner';

const AdminGgfSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [config, setConfig] = useState({
    pass_threshold_pct: 80,
    default_pct: 80,
    min_pct: 50,
    max_pct: 100,
    updated_at: null,
  });
  const [draft, setDraft] = useState(80);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/settings/ggf-threshold');
      setConfig(r.data);
      setDraft(r.data.pass_threshold_pct);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load GGF settings');
    } finally {
      setLoading(false);
    }
  };

  const save = async () => {
    const v = Number(draft);
    if (!Number.isFinite(v) || v < config.min_pct || v > config.max_pct) {
      toast.error(`Pass threshold must be between ${config.min_pct}% and ${config.max_pct}%.`);
      return;
    }
    setSaving(true);
    try {
      const r = await api.put('/admin/settings/ggf-threshold', { pass_threshold_pct: v });
      toast.success(`Pass threshold updated to ${r.data.pass_threshold_pct}%`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setDraft(config.default_pct);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin text-emerald-500" />
      </div>
    );
  }

  const dirty = Number(draft) !== Number(config.pass_threshold_pct);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-3xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="ggf-settings-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-emerald-600" />
            GGF Certification Settings
          </h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Pass Threshold</CardTitle>
            <CardDescription>
              The minimum score (percent) required for a talent to be marked Guardian
              Garage Floors Certified. Pricing-policy questions still auto-fail when
              missed, regardless of overall score.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <Alert className="border-emerald-200 bg-emerald-50 text-emerald-900">
              <Info className="w-4 h-4" />
              <AlertDescription>
                Default is {config.default_pct}%. Allowed range:
                {' '}{config.min_pct}% – {config.max_pct}%.
                Changes apply immediately to new assessment submissions.
              </AlertDescription>
            </Alert>

            <div className="space-y-2">
              <Label htmlFor="threshold-input" className="text-sm font-medium">
                Pass mark (percent)
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  id="threshold-input"
                  type="number"
                  inputMode="numeric"
                  step="1"
                  min={config.min_pct}
                  max={config.max_pct}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  className="w-32"
                  data-testid="ggf-threshold-input"
                />
                <span className="text-sm text-gray-500">%</span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={reset}
                  disabled={Number(draft) === Number(config.default_pct)}
                  data-testid="ggf-threshold-reset-btn"
                >
                  Reset to default ({config.default_pct}%)
                </Button>
              </div>
              <p className="text-xs text-gray-500">
                Currently active: <strong>{config.pass_threshold_pct}%</strong>
                {config.updated_at && (
                  <span className="ml-2 text-gray-400">
                    (updated {new Date(config.updated_at).toLocaleString()})
                  </span>
                )}
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-slate-100">
              <Button
                variant="outline"
                onClick={() => setDraft(config.pass_threshold_pct)}
                disabled={!dirty || saving}
                data-testid="ggf-threshold-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                onClick={save}
                disabled={!dirty || saving}
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="ggf-threshold-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                Save changes
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminGgfSettings;
